import { useState } from 'react';
import {
  getLatestWinners,
  getFeaturedWinners,
  getWinnersOfTheMonth,
  formatWinnerDate,
  getRelativeTime,
  getPositionLabel,
  winnerTypeLabels,
  winnerTypeIcons,
  winnerTypeColors,
  winnerGlobalStats,
  type RfwWinner,
} from '@/mocks/rfwWinners';

function WinnerCard({ winner, size = 'md' }: { winner: RfwWinner; size?: 'sm' | 'md' | 'lg' }) {
  const posInfo = getPositionLabel(winner.position);
  const typeColor = winnerTypeColors[winner.type];

  if (size === 'sm') {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-amber-300/50 transition-colors duration-200 cursor-pointer group">
        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-amber-200">
          <img src={winner.photoUrl} alt={winner.name} className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold text-foreground-950 truncate">{winner.name}</span>
            <span className={posInfo.color + ' text-xs'}>{posInfo.emoji}</span>
          </div>
          <p className="text-xs text-foreground-600 truncate">{winner.contestName}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${typeColor}`}>
              {winnerTypeLabels[winner.type]}
            </span>
            <span className="text-[10px] text-foreground-500">{getRelativeTime(winner.date)}</span>
          </div>
        </div>
      </div>
    );
  }

  if (size === 'lg') {
    return (
      <div className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-amber-300/50 transition-all duration-300 group cursor-pointer">
        <div className="relative h-36 bg-gradient-to-br from-amber-100 via-orange-50 to-yellow-100 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
          <img src={winner.photoUrl} alt={winner.name} className="w-24 h-24 rounded-full object-cover ring-4 ring-white shadow-lg relative z-10" />
          <div className="absolute top-3 right-3 z-10">
            <span className="text-2xl">{posInfo.emoji}</span>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColor}`}>
              <i className={`${winnerTypeIcons[winner.type]} mr-1 text-[10px]`}></i>
              {winnerTypeLabels[winner.type]}
            </span>
            {winner.featured && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Destacado</span>
            )}
          </div>
          <h4 className="font-bold text-foreground-950 mb-1">{winner.name}</h4>
          <p className="text-xs text-foreground-600 mb-2 line-clamp-2">{winner.contestName}</p>
          <div className="flex items-center justify-between text-xs text-foreground-500">
            <span><i className="ri-map-pin-line mr-1"></i>{winner.city}</span>
            <span>{formatWinnerDate(winner.date)}</span>
          </div>
          <div className="mt-3 pt-3 border-t border-background-200/70">
            <div className="flex items-center gap-1 text-xs text-amber-600 font-medium">
              <i className="ri-trophy-line"></i>
              <span>{winner.prize}</span>
            </div>
            <p className="text-[11px] text-foreground-500 mt-1 line-clamp-2">{winner.prizeDescription}</p>
          </div>
        </div>
      </div>
    );
  }

  /* md — default */
  return (
    <div className="rounded-lg bg-background-50 border border-background-200/70 p-4 hover:border-amber-300/50 transition-colors duration-200 cursor-pointer group">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-amber-200">
          <img src={winner.photoUrl} alt={winner.name} className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h4 className="font-semibold text-foreground-950 text-sm truncate">{winner.name}</h4>
            <span className={posInfo.color + ' text-sm flex-shrink-0'}>{posInfo.emoji}</span>
          </div>
          <p className="text-xs text-foreground-600 mt-0.5">
            <i className="ri-radio-line mr-1 text-[10px]"></i>{winner.radioName}
            {winner.programName && <span className="text-foreground-400"> · {winner.programName}</span>}
          </p>
        </div>
      </div>
      <p className="text-sm text-foreground-800 mb-2 line-clamp-2 font-medium">{winner.contestName}</p>
      <div className="flex items-center gap-2 mb-2">
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${typeColor}`}>
          {winnerTypeLabels[winner.type]}
        </span>
        <span className="text-[10px] text-foreground-500">
          <i className="ri-map-pin-line mr-0.5"></i>{winner.city}
        </span>
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-background-200/70">
        <div className="flex items-center gap-1 text-xs text-amber-600 font-medium">
          <i className="ri-trophy-line text-[10px]"></i>
          <span className="truncate max-w-[140px]">{winner.prize}</span>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-foreground-500">
          <span>+{winner.scoreEarned} pts</span>
          <span>·</span>
          <span>{getRelativeTime(winner.date)}</span>
        </div>
      </div>
    </div>
  );
}

export default function GanadoresSection() {
  const [activeTab, setActiveTab] = useState<'latest' | 'featured' | 'month'>('latest');

  const latest = getLatestWinners(6);
  const featured = getFeaturedWinners();
  const monthWinners = getWinnersOfTheMonth();

  const tabs = [
    { id: 'latest' as const, label: 'Últimos Ganadores', icon: 'ri-timer-line', count: latest.length },
    { id: 'featured' as const, label: 'Ganadores Destacados', icon: 'ri-star-line', count: featured.length },
    { id: 'month' as const, label: 'Ganadores del Mes', icon: 'ri-calendar-check-line', count: monthWinners.length },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            SISTEMA DE GANADORES RFW
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Cada Participación Tiene su
            <span className="text-amber-600"> Reconocimiento</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            No solo premiamos con cosas físicas — el reconocimiento social también es un premio.
            Cada ganador suma puntos, insignias y prestigio en la comunidad RFW.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Ganadores Totales', value: winnerGlobalStats.totalWinners.toLocaleString(), icon: 'ri-trophy-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Ganadores Hoy', value: winnerGlobalStats.winnersToday.toString(), icon: 'ri-calendar-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Puntos Otorgados', value: `${(winnerGlobalStats.totalPointsAwarded / 1000).toFixed(0)}K`, icon: 'ri-flashlight-line', color: 'text-orange-600 bg-orange-50' },
            { label: 'Leyendas RFW', value: winnerGlobalStats.leyendasCount.toString(), icon: 'ri-vip-crown-line', color: 'text-yellow-600 bg-yellow-50' },
            { label: 'Comp. Activas', value: winnerGlobalStats.activeCompetitions.toString(), icon: 'ri-sword-line', color: 'text-rose-600 bg-rose-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-background-100 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center flex-shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center gap-1 mb-8">
          <div className="inline-flex bg-background-100 rounded-full p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'
                }`}>{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {activeTab === 'latest' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {latest.map((winner) => (
              <WinnerCard key={winner.id} winner={winner} size="md" />
            ))}
          </div>
        )}

        {activeTab === 'featured' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
              {featured.slice(0, 3).map((winner, i) => (
                <WinnerCard key={winner.id} winner={winner} size="lg" />
              ))}
            </div>
            {featured.length > 3 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {featured.slice(3).map((winner) => (
                  <WinnerCard key={winner.id} winner={winner} size="sm" />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'month' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {monthWinners.map((winner) => (
              <WinnerCard key={winner.id} winner={winner} size="md" />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <a
            href="/ganadores"
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-trophy-line"></i>
            Ver Todos los Ganadores
            <i className="ri-arrow-right-line"></i>
          </a>
          <p className="text-xs text-foreground-500 mt-3">
            Explora el historial completo, filtra por tipo y descubre quiénes están haciendo historia en RFW
          </p>
        </div>
      </div>
    </section>
  );
}